import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { PendingRequestsInterceptor } from 'ng-http-loader';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { AppConstants } from '@app/@shared/app.constants';
import { BundleInfo, OrderJSON } from '@app/@shared/orderjson.model';

/**
 * Query-string marker appended to background prefetch requests only.
 *
 * The global `ng-http-loader` spinner is configured (see `AppComponent.filteredUrls`)
 * to ignore any request whose URL matches this pattern, so prefetching never shows a
 * loader. The same billing endpoints requested normally (without the marker) keep their
 * default spinner behaviour, and the backend simply ignores the extra param.
 */
export const PREFETCH_MARKER_PARAM = 'prefetchInBackground';
export const PREFETCH_MARKER_PATTERN = `${PREFETCH_MARKER_PARAM}=1`;

const DEFAULT_CURRENCY = 'USD';

/** Name of the CacheStorage cache under which resolved prefetch responses are persisted. */
const PREFETCH_CACHE_NAME = 'prefetch-cache';

/** Synthetic cache key under which the bundle/hardware selection signature is persisted. */
const SIGNATURE_STORAGE_KEY = '/__prefetch__/signature';

/**
 * Warms and caches the API data required by the Software Features and Equipment Services
 * pages so they render instantly once the user reaches them.
 *
 * Prefetching is triggered from the Business Information 2 page. Because this service is a
 * root singleton, the in-flight requests are owned by the service (not by any component),
 * so they keep running in the background even if the user immediately navigates back and
 * forth. Results are cached and reused; the cache is only refreshed when the selected
 * software bundle or hardware bundle changes. Resolved responses are mirrored to the
 * browser Cache API so they survive page reloads.
 */
@Injectable({ providedIn: 'root' })
export class PrefetchService {
  /** In-flight / shareReplay observables keyed by the canonical (marker-free) request URL. */
  private readonly cache = new Map<string, Observable<any>>();

  /** Signature of the bundle/hardware selection the cache was built for. */
  private signature: string | null = null;

  /** Cache keys whose shared response has already resolved (safe to serve without a loader). */
  private readonly ready = new Set<string>();

  /**
   * Tracks the asynchronous restore of the persisted signature from the Cache API.
   * Signature-gated prefetch logic awaits this so a page reload doesn't look like a
   * selection change and trigger a spurious cache wipe.
   */
  private signatureReady: Promise<void>;

  constructor(private http: HttpClient, private pendingRequests: PendingRequestsInterceptor) {
    this.signatureReady = this.loadSignature();
  }

  /**
   * Kick off background prefetching of the Software Features and Equipment Services data.
   * Safe to call every time BI2 is (re)entered: requests are fired at most once per
   * bundle/hardware selection.
   */
  prefetchNextPages(ramJson: OrderJSON | null | undefined): void {
    if (!ramJson) {
      return;
    }
    // These pages don't exist in the NRS Pay-only flow, so there is nothing to prefetch.
    if (ramJson.subscriptionType === 'NRSPAY') {
      return;
    }

    const bundleId = ramJson.bundleId;
    if (bundleId === null || bundleId === undefined) {
      return;
    }

    const bundleInfo: BundleInfo[] = ramJson.BundleInfo || [];
    const softwareBundle = bundleInfo.find((bundle: BundleInfo) => bundle?.type === 'S');
    const hardwareBundle = bundleInfo.find((bundle: BundleInfo) => bundle?.type === 'H');
    const softwareBundleId = softwareBundle?.bundleId;
    const hardwareBundleId = hardwareBundle?.bundleId;
    const unselectedSoftwareBundleId = softwareBundle?.unselectedSoftwareBundleId;

    // Only refresh the cache when the software bundle or hardware changes.
    const signature = [bundleId, softwareBundleId, hardwareBundleId, unselectedSoftwareBundleId].join('|');

    void this.applySignature(signature).then(() => {
      // Software Features - selected plan.
      if (softwareBundleId !== null && softwareBundleId !== undefined) {
        this.warm(this.additionalFeaturesUrl(bundleId, softwareBundleId, DEFAULT_CURRENCY));
      }

      // Software Features - unselected plan (used for the price comparison view).
      if (unselectedSoftwareBundleId !== null && unselectedSoftwareBundleId !== undefined) {
        const otherSubscriptionId =
          bundleId !== AppConstants.no_pay_nrs_subscriptionId
            ? AppConstants.no_pay_nrs_subscriptionId
            : AppConstants.nrs_pay_subscriptionId;
        this.warm(this.additionalFeaturesUrl(otherSubscriptionId, unselectedSoftwareBundleId, DEFAULT_CURRENCY));
      }

      // Software Features - NRS Pay features (EBT / eWIC).
      this.warm(this.nrsPayPlansUrl(bundleId, DEFAULT_CURRENCY));

      // Equipment Services.
      this.warm(this.equipmentServicesUrl(bundleId, DEFAULT_CURRENCY));
    });
  }

  /**
   * Additional features and bundles for a software plan.
   * Returns the prefetched (cached) response when available, otherwise fetches on demand.
   */
  getAdditionalFeaturesAndBundles(
    subscriptionTypeId: number,
    bundleId: number,
    currency: string = DEFAULT_CURRENCY,
  ): Observable<any> {
    return this.getOrCreate(this.additionalFeaturesUrl(subscriptionTypeId, bundleId, currency), false);
  }

  /** Software plans available for a hardware bundle. */
  getSoftwarePlans(bundleId: number, currency: string = DEFAULT_CURRENCY): Observable<any> {
    return this.getOrCreate(this.softwarePlansUrl(bundleId, currency), false);
  }

  /**
   * Equipment and services for a hardware bundle.
   * Returns the prefetched (cached) response when available, otherwise fetches on demand.
   */
  getEquipmentServices(bundleId: number, currency: string = DEFAULT_CURRENCY): Observable<any> {
    return this.getOrCreate(this.equipmentServicesUrl(bundleId, currency), false);
  }

  /**
   * NRS Pay plans / features for a bundle.
   * Returns the prefetched (cached) response when available, otherwise fetches on demand.
   */
  getNRSPayPlans(bundleId: number, currency: string = DEFAULT_CURRENCY): Observable<any> {
    return this.getOrCreate(this.nrsPayPlansUrl(bundleId, currency), false);
  }

  /**
   * Zoho banking form URL for the Bank page.
   * Returns the prefetched (cached) response when available, otherwise fetches on demand.
   */
  getBankForm(zohoEncryptedId: string, privilegeId: number): Observable<any> {
    return this.getOrCreatePost(
      this.bankFormCacheKey(zohoEncryptedId),
      this.bankFormRequestUrl(),
      this.bankFormBody(zohoEncryptedId, privilegeId),
      false,
    );
  }

  /**
   * Fire a background (spinner-free) request for the Zoho banking form URL so the
   * Bank page's iframe can start loading the moment the user lands on it.
   */
  prefetchBankForm(zohoEncryptedId: string, privilegeId: number): void {
    if (!zohoEncryptedId || privilegeId === null || privilegeId === undefined) {
      return;
    }
    this.warmPost(
      this.bankFormCacheKey(zohoEncryptedId),
      this.bankFormRequestUrl(),
      this.bankFormBody(zohoEncryptedId, privilegeId),
    );
  }


  /**
   * Hardware item images for a given set of billing item IDs.
   * Returns the cached response when images have already been prefetched, otherwise fetches on demand.
   * billingItemIds can be a comma-joined string or an array — both resolve to the same cache key.
   */
  getHardwareItemImages(billingItemIds: string | number[]): Observable<any> {
    return this.getOrCreate(this.imagesUrl(billingItemIds), false);
  }

  /**
   * Prefetch images for every tab in the equipment services categoriesMap.
   * Called as soon as the equipment services response arrives and the map is built,
   * so images for all tabs are warm before the user clicks any of them.
   */
  prefetchEquipmentImages(categoriesMap: { [key: string]: any[] }): void {
    Object.keys(categoriesMap).forEach(tab => {
      const items = categoriesMap[tab];
      if (!Array.isArray(items) || !items.length) {
        return;
      }
      const ids = items
        .map((item: any) => item.billingItemId)
        .filter((id: any) => id !== null && id !== undefined)
        .join(',');
      if (ids) {
        this.warm(this.imagesUrl(ids));
      }
    });
  }
  /** Fire a background (spinner-free) request and cache it. */
  private warm(canonicalUrl: string): void {
    this.getOrCreate(canonicalUrl, true);
  }

  /**
   * Return the cached observable for a URL, creating (and firing) it if needed.
   * `background` requests carry the prefetch marker so the global spinner ignores them.
   * The cache is keyed by the canonical (marker-free) URL so a foreground page request
   * reuses a previously prefetched response instead of issuing a new call.
   */
  private getOrCreate(canonicalUrl: string, background: boolean): Observable<any> {
    const cached = this.cache.get(canonicalUrl);
    if (cached) {
      return this.joinCached(canonicalUrl, cached, !background);
    }

    const requestUrl = background ? this.withPrefetchMarker(canonicalUrl) : canonicalUrl;
    const network$ = this.http
      .get(requestUrl)
      .pipe(tap((data) => this.persist(canonicalUrl, data)));

    const shared = this.shareRestorable(canonicalUrl, network$);
    this.cache.set(canonicalUrl, shared);
    return this.joinCached(canonicalUrl, shared, !background);
  }

  /**
   * Serve a cached observable. A foreground caller that joins a still-pending (background,
   * marker-filtered) request is routed through the loader interceptor so the global spinner
   * shows until it resolves; already-resolved entries stay loader-free.
   */
  private joinCached(key: string, shared: Observable<any>, trackLoader: boolean): Observable<any> {
    const copy$ = shared.pipe(map((data) => cloneDeep(data)));
    if (!trackLoader) {
      return copy$;
    }
    return defer(() => {
      if (this.ready.has(key)) {
        return copy$;
      }
      return this.pendingRequests.intercept(new HttpRequest('GET', key), {
        handle: () => copy$ as Observable<HttpEvent<any>>,
      });
    });
  }

  private withPrefetchMarker(url: string): string {
    return url + (url.includes('?') ? '&' : '?') + PREFETCH_MARKER_PATTERN;
  }

  /** Fire a background (spinner-free) POST request and cache it. */
  private warmPost(cacheKey: string, requestUrl: string, body: any): void {
    this.getOrCreatePost(cacheKey, requestUrl, body, true);
  }

  /**
   * Return the cached observable for a POST request, creating (and firing) it if needed.
   * The cache key can differ from the actual request URL (the bank form request always
   * posts to `/zoho/bankInfo` while the cache is keyed per `zohoEncryptedId`).
   */
  private getOrCreatePost(cacheKey: string, requestUrl: string, body: any, background: boolean): Observable<any> {
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return this.joinCached(cacheKey, cached, false);
    }

    const url = background ? this.withPrefetchMarker(requestUrl) : requestUrl;
    const network$ = this.http
      .post(url, body)
      .pipe(tap((data) => this.persist(cacheKey, data)));

    const shared = this.shareRestorable(cacheKey, network$);
    this.cache.set(cacheKey, shared);
    return shared.pipe(map((data) => cloneDeep(data)));
  }

  /**
   * Wrap a network observable so the first subscription checks the Cache API for a
   * previously persisted response (e.g. after a page reload) and only hits the network
   * on a miss. The result is shared with `shareReplay` and the request is triggered
   * immediately, so it runs in the background even before any page subscribes. Failures
   * are evicted from the in-memory map so a later attempt can retry.
   */
  private shareRestorable(cacheKey: string, network$: Observable<any>): Observable<any> {
    const shared = defer(() => this.readEntry(cacheKey)).pipe(
      switchMap((stored) => (stored !== undefined ? of(stored) : network$)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    shared.subscribe({
      next: () => {
        if (this.cache.get(cacheKey) === shared) {
          this.ready.add(cacheKey);
        }
      },
      error: () => this.cache.delete(cacheKey),
    });

    return shared;
  }

  private bankFormCacheKey(zohoEncryptedId: string): string {
    return `/zoho/bankInfo/${zohoEncryptedId}`;
  }

  private bankFormRequestUrl(): string {
    return `/zoho/bankInfo`;
  }

  private bankFormBody(zohoEncryptedId: string, privilegeId: number): any {
    return { privilegeId, zohoEncryptedId };
  }

  private imagesUrl(billingItemIds: string | number[]): string {
    const ids = Array.isArray(billingItemIds) ? billingItemIds.join(',') : billingItemIds;
    return `/images/${ids}`;
  }

  private additionalFeaturesUrl(subscriptionTypeId: number, bundleId: number, currency: string): string {
    return `/billingItems/${subscriptionTypeId}/${bundleId}/${currency}`;
  }

  private softwarePlansUrl(bundleId: number, currency: string): string {
    return `/bundles/${bundleId}/${currency}`;
  }

  private equipmentServicesUrl(bundleId: number, currency: string): string {
    return `/billingItems/EQSW/${bundleId}/${currency}`;
  }

  private nrsPayPlansUrl(bundleId: number, currency: string): string {
    return `/billingItems/NrsPay/${bundleId}/${currency}`;
  }

  /**
   * Wipe the in-memory cache and the persisted Cache API copy. Called when the user
   * reaches the landing, login or success pages so a fresh order flow starts clean.
   */
  clearCache(): void {
    this.cache.clear();
    this.ready.clear();
    this.signature = null;
    // Chain after any in-flight signature restore so the cleared state always wins.
    this.signatureReady = this.signatureReady.then(() => {
      this.signature = null;
      return this.wipeStore();
    });
  }

  /**
   * Apply a new bundle/hardware signature once the persisted signature has been
   * restored. On a mismatch the in-memory cache and the persisted store are wiped
   * and the new signature is persisted before any new requests are warmed.
   */
  private async applySignature(next: string): Promise<void> {
    await this.signatureReady;
    if (next === this.signature) {
      return;
    }
    this.cache.clear();
    this.ready.clear();
    this.signature = next;
    await this.wipeStore();
    this.persist(SIGNATURE_STORAGE_KEY, next);
  }

  /** Load the persisted signature from the Cache API on startup. */
  private async loadSignature(): Promise<void> {
    const stored = await this.readEntry(SIGNATURE_STORAGE_KEY);
    this.signature = typeof stored === 'string' ? stored : null;
  }

  /** Persist a resolved response to the Cache API (fire-and-forget). */
  private persist(key: string, data: any): void {
    if (data === undefined) {
      return;
    }
    void this.openStore()
      .then((store) => {
        if (!store) {
          return;
        }
        const response = new Response(JSON.stringify(data), {
          headers: { 'Content-Type': 'application/json' },
        });
        return store.put(key, response);
      })
      .catch(() => {
        /* Ignore serialization / quota errors — the in-memory cache still works. */
      });
  }

  /** Read a previously persisted response body from the Cache API. */
  private async readEntry(key: string): Promise<any> {
    const store = await this.openStore();
    if (!store) {
      return undefined;
    }
    try {
      const response = await store.match(key);
      if (!response) {
        return undefined;
      }
      return await response.json();
    } catch {
      return undefined;
    }
  }

  /** Delete the whole persisted prefetch store (signature included). */
  private async wipeStore(): Promise<void> {
    if (!this.isCacheApiAvailable()) {
      return;
    }
    try {
      await caches.delete(PREFETCH_CACHE_NAME);
    } catch {
      /* Cache API unavailable — the in-memory cache has already been cleared. */
    }
  }

  /** Open the prefetch cache, or return null when the Cache API is unavailable. */
  private async openStore(): Promise<Cache | null> {
    if (!this.isCacheApiAvailable()) {
      return null;
    }
    try {
      return await caches.open(PREFETCH_CACHE_NAME);
    } catch {
      return null;
    }
  }

  private isCacheApiAvailable(): boolean {
    return typeof caches !== 'undefined' && caches !== null;
  }
}
